import { Button } from '@aqarmap/aqarmap-ui';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import View from '~/modules/addListing/pages/DetailsStep/View';
import FinishType from '~/modules/addListing/pages/DetailsStep/FinishType';
import {
  useAddListingDetailsMutation,
  useGetListingCustomFieldsQuery,
  useGetListingDetailsQuery,
  useGetUserQuery,
} from '~/redux/api/userApiSlice';
import Loader from '~/components/Loader';
import {
  addListingDetailsType,
  ListingDetailsType,
} from '~/modules/addListing/types';
import RoomsNumber from '~/modules/addListing/pages/DetailsStep/RoomsNumber';
import Area from '~addListing/pages/DetailsStep/Area';
import BathesNumber from '~addListing/pages/DetailsStep/BathesNumber';
import Floor from '~addListing/pages/DetailsStep/Floor';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  listingDetailsSchemaForSale,
  listingDetailsSchemaForRent,
} from '~addListing/utils/schema';
import BuildYear from '~addListing/pages/DetailsStep/BuildYear';
import Floors from '~addListing/pages/DetailsStep/Floors';
import Apartments from '~addListing/pages/DetailsStep/Apartments';
import LandArea from '~addListing/pages/DetailsStep/LandArea';
import PropertyRegistrationStatus from '~addListing/pages/DetailsStep/PropertyRegistrationStatus';
import { saveListingData } from '~addListing/utils/LocalStorage';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useFullRoutes } from '~/hooks';
import Amenities from '~addListing/pages/DetailsStep/Amenities';
import { getLocalizedListingPrefill } from '~addListing/utils/getLocalizedListingPrefill';

const DetailsStep = () => {
  const { id } = useParams();
  const { t } = useTranslation('app');
  const navigate = useNavigate();
  const { ADD_LISTING_URL } = useFullRoutes();
  const [errorAttributeIds, setErrorAttributeIds] = React.useState<number[]>(
    [],
  );
  const { data: userData } = useGetUserQuery();
  const { data, isSuccess, refetch, isFetching } = useGetListingDetailsQuery(
    id,
    {
      refetchOnMountOrArgChange: true,
    },
  );
  const { data: customFieldsData, isLoading: isLoadingCustomField } =
    useGetListingCustomFieldsQuery(null);
  const [addListingDetails, { isLoading: isLoadingAddListingDetails }] =
    useAddListingDetailsMutation();
  const listingDetails: ListingDetailsType = data?.listing;
  const isForSale = listingDetails?.section.slug === 'for-sale';

  const methods = useForm<addListingDetailsType>({
    resolver: zodResolver(
      isForSale ? listingDetailsSchemaForSale : listingDetailsSchemaForRent,
    ),
  });

  const getAttributesWithErrors = () => {
    const attributes = [...(methods.getValues('attributes') || [])];
    const errorIds = attributes
      .filter((att) => att.value === 0)
      .map((att) => att.customField);
    setErrorAttributeIds(errorIds);
    return errorIds ?? [];
  };

  const onSubmit = async (data: any) => {
    const errorIds = getAttributesWithErrors();
    if (errorIds.length === 0) {
      const addListingDetailsData = saveListingData(`${id}`, data);
      const res = await addListingDetails({
        id,
        body: addListingDetailsData.data,
      });
      if ('data' in res && id) navigate(ADD_LISTING_URL.PRICE(id));
      if ('error' in res) {
        const error = res.error as FetchBaseQueryError;
        alert('error message: ' + (error.data as any)?.message);
      }
    }
  };

  useEffect(() => {
    if (!isFetching && !isSuccess) navigate(ADD_LISTING_URL.INITIALIZE);

    if (Object.keys(methods.formState.errors).length > 0)
      getAttributesWithErrors();
    if (isSuccess) {
      const isOwner = listingDetails?.user.id === userData?.user?.id;
      const isPublisher =
        listingDetails?.publisher_user?.id === userData?.user?.id;
      if (!isOwner && !isPublisher) {
        navigate(ADD_LISTING_URL.INITIALIZE);
      }
    }
  }, [isSuccess, isFetching, methods.formState.errors]);

  useEffect(() => {
    if (listingDetails) {
      methods.setValue('area', Number(listingDetails.area));
      methods.setValue('price', Number(listingDetails.price));

      methods.setValue(
        'marketPropertyType',
        listingDetails.market_property_type,
      );

      methods.setValue('propertyView', listingDetails.property_view);
    }
  }, [isSuccess, listingDetails]);

  useEffect(() => {
    refetch();
  }, []);

  useEffect(() => {
    if (listingDetails) {
      const localizedPrefill = getLocalizedListingPrefill({
        translations: listingDetails.translations,
      });

      let defaultData: Record<string, any> = {
        ...localizedPrefill,
        price: Number(listingDetails.price) || 0,
        ...(listingDetails.payment_method
          ? { paymentMethod: listingDetails.payment_method }
          : {}),
        paymentPlans:
          listingDetails.payment_plans?.map((plan) => ({
            downPayment: plan.down_payment,
            duration: plan.duration,
            price: plan.price,
            overPrice: plan.over_price,
            securityDeposit: plan.security_deposit,
          })) || [],
        videoUrl: listingDetails.video_url || '',
      };
      Object.keys(defaultData).forEach((key) => {
        const value = defaultData[key];
        if (value === '' || value === null || value === 0) {
          delete defaultData[key];
        }
      });

      saveListingData(`${id}`, defaultData);
    }
  }, [listingDetails]);

  if (isFetching || isLoadingCustomField) return <Loader />;

  return (
    <div className="flex flex-col gap-4x w-full">
      <p className="text-title_2">{t('detailsOfListing')}</p>
      <FormProvider {...methods}>
        <div className="flex flex-col gap-5x md:gap-6x">
          <div className="flex flex-col gap-4x">
            <Area />
            <LandArea
              listing={listingDetails}
              customFieldsData={customFieldsData}
              errorAttributeIds={errorAttributeIds}
              setErrorAttributeIds={setErrorAttributeIds}
            />
            <RoomsNumber
              listing={listingDetails}
              customFieldsData={customFieldsData}
              errorAttributeIds={errorAttributeIds}
              setErrorAttributeIds={setErrorAttributeIds}
            />
            <BathesNumber
              listing={listingDetails}
              customFieldsData={customFieldsData}
              errorAttributeIds={errorAttributeIds}
              setErrorAttributeIds={setErrorAttributeIds}
            />
            <Floor
              listing={listingDetails}
              customFieldsData={customFieldsData}
              errorAttributeIds={errorAttributeIds}
              setErrorAttributeIds={setErrorAttributeIds}
            />
            <Floors
              listing={listingDetails}
              customFieldsData={customFieldsData}
              errorAttributeIds={errorAttributeIds}
              setErrorAttributeIds={setErrorAttributeIds}
            />
            <Apartments
              listing={listingDetails}
              customFieldsData={customFieldsData}
              errorAttributeIds={errorAttributeIds}
              setErrorAttributeIds={setErrorAttributeIds}
            />
            <BuildYear
              customFieldsData={customFieldsData}
              listing={listingDetails}
              errorAttributeIds={errorAttributeIds}
              setErrorAttributeIds={setErrorAttributeIds}
            />
            <View customFieldsData={customFieldsData} />
            <FinishType
              customFieldsData={customFieldsData}
              listing={listingDetails}
              errorAttributeIds={errorAttributeIds}
              setErrorAttributeIds={setErrorAttributeIds}
            />
            <PropertyRegistrationStatus
              listing={listingDetails}
              customFieldsData={customFieldsData}
            />
          </div>
          <Amenities listing={listingDetails} />
          <div>
            <Button
              onClick={methods.handleSubmit(onSubmit)}
              variant="primary"
              size="medium"
              className="w-[135px]"
              isLoading={isLoadingAddListingDetails}
            >
              {t('follow')}
            </Button>
          </div>
        </div>
      </FormProvider>
    </div>
  );
};

export default DetailsStep;
